import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Trash, UserPlus, FolderSimple, Eye, FileArrowDown } from "@phosphor-icons/react";
import { SuiteBadge } from "@/components/projects/SuiteBadge";
import { useAuth } from "@/context/AuthContext";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "-");

export const ProjectsTable = ({ projects, cfg, selIds, onToggleSel, onSelectAll, onOpen, onBrief, onEdit, onMove, onClientAccount, onDelete }) => {
  const { hasPerm } = useAuth();
  return (
    <Card className="shadow-none overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="projects-table">
          <thead>
            <tr className="text-left border-b border-border">
              {hasPerm("delete_project") && (
                <th className="px-4 py-3 w-8">
                  <input type="checkbox" data-testid="projects-select-all" className="h-4 w-4 accent-primary"
                    checked={selIds.length > 0 && selIds.length === projects.length}
                    onChange={(e) => onSelectAll(e.target.checked)} />
                </th>
              )}
              <th className="px-4 py-3 overline font-medium">Project Name</th>
              <th className="px-4 py-3 overline font-medium">Client</th>
              {cfg.commodity && <th className="px-4 py-3 overline font-medium">Commodity</th>}
              <th className="px-4 py-3 overline font-medium whitespace-nowrap">Last Modified</th>
              <th className="px-4 py-3 overline font-medium">Status</th>
              <th className="px-4 py-3 w-24"></th>
            </tr>
          </thead>
          <tbody>
            {projects.map((p) => (
              <tr key={p.id} data-testid={`project-row-${p.id}`} onClick={() => onOpen(p)}
                className="border-b border-border/60 hover:bg-accent/40 cursor-pointer group">
                {hasPerm("delete_project") && (
                  <td className="px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" data-testid={`project-check-${p.id}`} checked={selIds.includes(p.id)}
                      onChange={() => onToggleSel(p.id)} className="h-4 w-4 accent-primary" />
                  </td>
                )}
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold group-hover:text-primary transition-colors">{p.name}</span>
                    <SuiteBadge commodity={p.commodity} id={p.id} hms={p.hms} />
                  </div>
                  <div className="text-[11px] text-muted-foreground font-mono">
                    {p.project_number && <span data-testid={`project-number-${p.id}`}>{p.project_number}</span>}
                    {p.project_number && p.country && " · "}{p.country}
                  </div>
                  {(p.shared || p.share_views > 0) && (
                    <span data-testid={`project-views-${p.id}`} title="Client teaser opens"
                      className="mt-1 inline-flex items-center gap-1 rounded-full bg-orange-50 text-orange-700 border border-orange-200 px-1.5 py-0.5 text-[10px] font-semibold">
                      <Eye size={11} /> {p.share_views || 0} {p.share_views === 1 ? "open" : "opens"}
                    </span>
                  )}
                </td>
                <td className="px-4 py-2.5 text-muted-foreground">{p.client_name || "-"}</td>
                {cfg.commodity && <td className="px-4 py-2.5"><Badge variant="secondary" className="font-mono text-[10px]">{p.commodity || "-"}</Badge></td>}
                <td className="px-4 py-2.5 text-muted-foreground whitespace-nowrap font-mono text-xs">{fmtDate(p.updated_at)}</td>
                <td className="px-4 py-2.5">
                  <Badge className={
                    p.status === "Completed" ? "bg-priority-low text-white" :
                    p.status === "Processing" ? "bg-priority-med text-black" : ""} variant="outline">{p.status}</Badge>
                </td>
                <td className="px-4 py-2.5">
                  <div className="flex items-center justify-end gap-1">
                    <button data-testid={`brief-${p.id}`} title="Download Command Brief (1-page technical PDF)"
                      onClick={(e) => { e.stopPropagation(); onBrief(p); }}
                      className="p-1.5 rounded-md text-muted-foreground opacity-0 group-hover:opacity-100 hover:bg-primary/10 hover:text-primary transition-all">
                      <FileArrowDown size={15} />
                    </button>
                    {hasPerm("edit_project") && (
                      <button data-testid={`edit-settings-${p.id}`} title="Rename / report settings"
                        onClick={(e) => { e.stopPropagation(); onEdit(p); }}
                        className="p-1.5 rounded-md text-muted-foreground opacity-0 group-hover:opacity-100 hover:bg-primary/10 hover:text-primary transition-all">✎</button>
                    )}
                    {hasPerm("edit_project") && (
                      <button data-testid={`move-folder-${p.id}`} title="Move to folder"
                        onClick={(e) => { e.stopPropagation(); onMove(p); }}
                        className="p-1.5 rounded-md text-muted-foreground opacity-0 group-hover:opacity-100 hover:bg-primary/10 hover:text-primary transition-all">
                        <FolderSimple size={15} />
                      </button>
                    )}
                    {hasPerm("manage_users") && (
                      <button data-testid={`client-account-${p.id}`} title="Create client account for this project"
                        onClick={(e) => { e.stopPropagation(); onClientAccount(p); }}
                        className="p-1.5 rounded-md text-muted-foreground opacity-0 group-hover:opacity-100 hover:bg-primary/10 hover:text-primary transition-all">
                        <UserPlus size={15} />
                      </button>
                    )}
                    {hasPerm("delete_project") && (
                      <button data-testid={`delete-project-${p.id}`} title="Delete project"
                        onClick={(e) => { e.stopPropagation(); onDelete(p); }}
                        className="p-1.5 rounded-md text-muted-foreground opacity-0 group-hover:opacity-100 hover:bg-destructive/10 hover:text-destructive transition-all">
                        <Trash size={15} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
};
